import { lazy, Suspense, useEffect } from "react";

import { useUFOComponentExperience } from "@atlassian/ufo";

import SignupModule from "@/components/signup/bxp-ui-signup/src/signup-module";
import { FeatureFlags } from "@/components/signup/bxpkit-signup/src/model";
import { LayoutType } from "@/components/signup/bxpkit-signup/src/sign-up-layout-container";
import { Bundle, Edition } from "@/components/signup/utils";
import { ufoEventPaidSignupLandingPageLoad } from "@/components/signup/utils";
import TryIntent from "@/components/try-intent/src";
import {
    useConfluenceIntent,
    useDevAiIntent,
    useLoomIntent,
    useJiraIntent,
    useJiraServiceManagementIntent,
    useJiraProductDiscoveryIntent,
    useNewUser,
    useProductIntent,
    useServiceCollectionIntent,
    useTeamworkCollectionIntent,
    useUserContext
} from "@/components/user-context";
import {
    AA_AUTOCOMPLETE_POTENTIAL_CONVERSION_EVENT_LOCAL_STORAGE_KEY,
    sendConversionEventAlgolia
} from "@/patterns/global-nav/src/auto-complete-helpers";
import {
    defaultFeatureFlags,
    defaultLabels,
    defaultProductData,
    getQueryParams,
    firstImpressionsImageUrls,
    useLabelsLocalized
} from "@/templates/try/cloud/signup/signup-page/src/utils";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { getLocalStorage } from "@/utils/localstorage";

import { ClientOnly, useLocation } from "../../../../../../utils/";

import { ErrorLocale } from "./components";
import { ConfluenceSignup } from "./confluence/confluence-signup";
import { DevAiSignup } from "./dev-ai/dev-ai-signup";
import { JiraSignup } from "./jira/jira-signup";
import { JpdSignup } from "./jira-product-discovery/jpd-signup";
import { JsmSignup } from "./jira-service-management/jsm-signup";
import { LoomSignup } from "./loom/loom-signup";
import { ServiceCollectionSignup } from "./service-collection/service-collection-signup";
import { TeamworkCollectionSignup } from "./teamwork-collection/teamwork-collection-signup";
import { handleSignupRouteRedirects } from "./utils/signup-redirection-resolver";

const SignUpLayoutContainer = lazy(
    () =>
        // @ts-ignore
        import(
            "../../../../../../components/signup/bxpkit-signup/src/sign-up-layout-container.tsx"
        )
);

type SignupPageTemplateProps = {
    layout?: LayoutType;
    featureFlags?: FeatureFlags;
};
export type SignupPageTemplateBaseProps = {
    product?: string;
    bundle?: string;
    migrationSourceUuid?: string;
    edition?: string;
    layout?: LayoutType;
    featureFlags?: FeatureFlags;
    isN2NParam?: boolean;
    useBlackSubmitButtonText?: boolean;
    developer?: string;
    shouldForceOverlayView?: boolean;
};

export const SignupPageTemplate = ({
    featureFlags
}: SignupPageTemplateProps) => {
    const location = useLocation();
    const { bundle: useProductIntentBundle } = useProductIntent(location.href);

    const {
        product,
        bundle,
        edition,
        isN2NParam,
        migrationSourceUuid,
        layout,
        developer,
        shouldForceOverlayView
    } = getQueryParams(location.search);

    if (handleSignupRouteRedirects({ product: product as string, bundle })) {
        return <></>;
    }

    return (
        <SignupPageTemplateBase
            product={product ?? undefined}
            bundle={useProductIntentBundle}
            edition={edition}
            layout={layout}
            featureFlags={{
                ...defaultFeatureFlags,
                ...featureFlags
            }}
            migrationSourceUuid={migrationSourceUuid}
            isN2NParam={isN2NParam}
            developer={developer}
            shouldForceOverlayView={shouldForceOverlayView}
        />
    );
};

export const SignupPageTemplateBase = ({
    product,
    bundle,
    edition,
    layout,
    featureFlags,
    isN2NParam,
    migrationSourceUuid,
    developer,
    shouldForceOverlayView
}: SignupPageTemplateBaseProps) => {
    useUFOComponentExperience(ufoEventPaidSignupLandingPageLoad);

    return (
        <Suspense>
            <div>
                <SignUpComponent
                    productParam={product}
                    bundleOverride={bundle}
                    editionOverride={edition}
                    layout={layout}
                    featureFlags={{
                        ...defaultFeatureFlags,
                        ...featureFlags
                    }}
                    isN2NParam={isN2NParam}
                    migrationSourceUuid={migrationSourceUuid}
                    developer={developer}
                    shouldForceOverlayView={shouldForceOverlayView}
                />
            </div>
        </Suspense>
    );
};

// TODO: Temporary fix for hot-120109
// eslint-disable-next-line complexity
const SignUpComponent = ({
    productParam,
    bundleOverride,
    editionOverride,
    layout = "first-impressions",
    featureFlags,
    migrationSourceUuid,
    developer,
    shouldForceOverlayView
}: {
    productParam?: string;
    bundleOverride?: string;
    editionOverride?: string;
    migrationSourceUuid?: string;
    layout?: LayoutType;
    isN2NParam?: boolean;
    developer?: string;
    featureFlags: FeatureFlags;
    shouldForceOverlayView?: boolean;
}) => {
    const { user: userProfile } = useUserContext();
    const isNewUser = useNewUser();
    const isJSW = useJiraIntent();
    const isJSM = useJiraServiceManagementIntent();
    const isJPD = useJiraProductDiscoveryIntent();
    const isDevAI = useDevAiIntent();
    const isLoom = useLoomIntent();
    const isConfluence = useConfluenceIntent();
    const isServiceCollection = useServiceCollectionIntent();
    const isTeamworkCollection = useTeamworkCollectionIntent();

    const countryCode =
        typeof window !== "undefined" ? window.countryCode : undefined;
    const blockedLocales = ["RU", "BY"];
    const labels = useLabelsLocalized(defaultLabels);

    useEffect(() => {
        const getaaCompleteQueryObj = getLocalStorage(
            AA_AUTOCOMPLETE_POTENTIAL_CONVERSION_EVENT_LOCAL_STORAGE_KEY
        );
        if (getaaCompleteQueryObj) {
            const parsedStringifyAttributes = JSON.parse(getaaCompleteQueryObj);
            sendConversionEventAlgolia({
                objectID: parsedStringifyAttributes.objectID,
                queryID: parsedStringifyAttributes.queryID,
                position: parsedStringifyAttributes.position
            });
        }
    }, []);

    if (countryCode && blockedLocales.includes(countryCode)) {
        return <ErrorLocale locale={countryCode} />;
    }

    if (userProfile.requestInProgress) {
        return <></>;
    }

    if (layout !== "standalone" && !migrationSourceUuid) {
        if (isJSW) {
            return <JiraSignup />;
        }

        if (isJSM) {
            return <JsmSignup />;
        }

        if (isJPD) {
            return <JpdSignup />;
        }

        if (isDevAI) {
            return <DevAiSignup />;
        }

        if (isConfluence) {
            return <ConfluenceSignup />;
        }

        if (isLoom) {
            return (
                <LoomSignup shouldForceOverlayView={shouldForceOverlayView} />
            );
        }

        if (isServiceCollection) {
            return <ServiceCollectionSignup />;
        }

        if (isTeamworkCollection) {
            return <TeamworkCollectionSignup />;
        }
    }

    return shouldShowBxpUi({
        productParam,
        bundleOverride,
        editionOverride,
        isNewUser,
        layout,
        migrationSourceUuid,
        developer
    }) ? (
        <ClientOnly>
            <SignupModule
                bundle={bundleOverride as Bundle}
                edition={editionOverride as Edition}
                isNewUser={isNewUser}
            />
        </ClientOnly>
    ) : (
        <ClientOnly>
            <TryIntent
                intentType="form"
                productKey={bundleOverride}
                edition={editionOverride}
            >
                <SignUpLayoutContainer
                    layout={layout}
                    allowSignupWithGoogle={true}
                    allowSignupWithAllSocialOptions={true}
                    bundleOverride={bundleOverride}
                    editionOverride={editionOverride}
                    productData={defaultProductData}
                    enableFreeBreachTest={true}
                    labels={labels}
                    firstImpressionsImageUrls={firstImpressionsImageUrls}
                    featureFlags={featureFlags}
                />
            </TryIntent>
        </ClientOnly>
    );
};

export function shouldShowBxpUi({
    productParam,
    bundleOverride,
    editionOverride,
    layout,
    migrationSourceUuid,
    developer
}: {
    productParam?: string;
    bundleOverride?: string;
    editionOverride?: string;
    migrationSourceUuid?: string;
    developer?: string;
    isNewUser?: boolean;
    layout?: LayoutType;
}) {
    // the flows that return true here have already been split off above
    // this gate gives us confidence to remove this function and always return false
    if (checkFeatureFlagGate(featureFlags.signupModuleDeprecation)) {
        return false;
    }

    const isBxpkitOnly = () => {
        // show Bxpkit to all users with the standalone layout (paid embedded)
        if (layout === "standalone") {
            return true;
        }
        if (migrationSourceUuid) {
            return true;
        }
        if (developer) {
            return true;
        }

        const isMultiProductsParam = productParam?.includes(",");
        if (isMultiProductsParam) {
            return true;
        }
        return false;
    };

    if (isBxpkitOnly()) {
        return false;
    }

    const isJsw = bundleOverride === "jira-software" || !bundleOverride;
    const isConfluence = bundleOverride === "confluence";
    const isJsm = bundleOverride === "jira-service-management";

    const isFreeEdition = editionOverride === "free" || !editionOverride;

    // show Bxpkit if not one of three core products
    const isBxpUiSupportedBundle = isConfluence || isJsw || isJsm;
    if (!isBxpUiSupportedBundle) {
        return false;
    }

    // Pre-filter conditions for site picker / return to product experiment
    const isEligibleForSitePicker = (isJsw || isConfluence) && isFreeEdition;
    if (isEligibleForSitePicker) {
        return true;
    }

    return false;
}
